import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import type { DirectionsTravelResult } from '../services/leafletUtils';

export type { DirectionsTravelResult };

const { __environment__ } = window as any;
const googleMapsKey = __environment__.REACT_APP_GOOGLE_MAPS_KEY;

type DirectionsEndpoint = string | google.maps.LatLng;

interface GoogleMapsContextProps {
  core?: google.maps.CoreLibrary;
  routes?: google.maps.RoutesLibrary;
  places?: google.maps.PlacesLibrary;
  getDirections: (
    origin: DirectionsEndpoint,
    destination: DirectionsEndpoint,
  ) => Promise<DirectionsTravelResult | undefined>;
}

export const GoogleMapsContext = createContext<GoogleMapsContextProps>({
  core: undefined,
  routes: undefined,
  places: undefined,
  getDirections: async () => undefined,
});

type RouteWaypoint =
  | { location: { latLng: { latitude: number; longitude: number } } }
  | { address: string };

type ComputeRoutesRequest = {
  origin: RouteWaypoint;
  destination: RouteWaypoint;
  travelMode: 'DRIVE';
  routingPreference?: 'TRAFFIC_AWARE' | 'TRAFFIC_UNAWARE';
};

type ComputeRoutesResponse = {
  routes: Array<{
    legs: Array<{
      distanceMeters: number;
      duration: string;
      localizedValues?: {
        distance: { text: string };
        duration: { text: string };
      };
    }>;
    polyline: { encodedPolyline: string };
    localizedValues?: {
      distance: { text: string };
      duration: { text: string };
    };
  }>;
};

type RouteLibraryWithComputeRoutes = google.maps.RoutesLibrary & {
  Route: {
    computeRoutes(request: ComputeRoutesRequest): Promise<ComputeRoutesResponse>;
  };
};

function toWaypoint(endpoint: DirectionsEndpoint): RouteWaypoint {
  if (typeof endpoint === 'string') {
    return { address: endpoint };
  }
  return {
    location: {
      latLng: { latitude: endpoint.lat(), longitude: endpoint.lng() },
    },
  };
}

function formatDurationText(durationStr: string): string {
  const seconds = parseInt(durationStr, 10);
  if (isNaN(seconds)) return durationStr;
  const minutes = Math.round(seconds / 60);
  return minutes === 1 ? '1 min' : `${minutes} mins`;
}

function formatDistanceText(meters: number): string {
  const miles = meters / 1609.344;
  return `${miles.toFixed(1)} mi`;
}

export const GoogleMapsContextProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const [core, setCore] = useState<google.maps.CoreLibrary>();
  const [places, setPlaces] = useState<google.maps.PlacesLibrary>();
  const [routes, setRoutes] = useState<google.maps.RoutesLibrary>();

  useEffect(() => {
    const loader = new Loader({
      apiKey: googleMapsKey,
      version: 'weekly',
      libraries: ['core', 'places', 'routes'],
    });

    loader.importLibrary('core').then(setCore);
    loader.importLibrary('places').then(setPlaces);
    loader.importLibrary('routes').then(setRoutes);
  }, []);

  const getDirections = useCallback(
    async (origin: DirectionsEndpoint, destination: DirectionsEndpoint) => {
      if (!routes) return undefined;

      const { Route } = routes as RouteLibraryWithComputeRoutes;
      if (!Route) return undefined;

      try {
        const request: ComputeRoutesRequest = {
          origin: toWaypoint(origin),
          destination: toWaypoint(destination),
          travelMode: 'DRIVE',
          routingPreference: 'TRAFFIC_AWARE',
        };

        const res = await Route.computeRoutes(request);

        return {
          routes: res.routes.map((route) => ({
            legs: route.legs.map((leg) => ({
              distance: {
                text:
                  leg.localizedValues?.distance.text ??
                  formatDistanceText(leg.distanceMeters),
                value: leg.distanceMeters,
              },
              duration: {
                text:
                  leg.localizedValues?.duration.text ??
                  formatDurationText(leg.duration),
                value: parseInt(leg.duration, 10) || 0,
              },
            })),
            overviewPolyline: route.polyline.encodedPolyline,
          })),
        } satisfies DirectionsTravelResult;
      } catch {
        // Failed to fetch directions
      }
    },
    [routes],
  );

  return (
    <GoogleMapsContext.Provider value={{ core, routes, places, getDirections }}>
      {children}
    </GoogleMapsContext.Provider>
  );
};

export const useGoogleMaps = () => useContext(GoogleMapsContext);
